import { useCallback, useEffect, useRef, useState } from "react";
import { OAuthServiceType } from "@common/constants";
import { SendingDomainMethod } from "@common/types/domains/emailSending";
import type { SendingDomainResponse } from "@common/schemas/sending-domain.schema";
import { useCreateSendingDomain } from "@api/sendingDomain.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";

const POPUP_CLOSE_GRACE_MS = 1_000;

interface UseConnectGmailOptions {
    /** Called after the Gmail sending domain is successfully created. */
    onConnected?: (domain: SendingDomainResponse) => void;
}

interface UseConnectGmailResult {
    /** Opens the Gmail OAuth popup and, on success, creates the sending domain. */
    connect: () => void;
    /** True while the OAuth popup is open or the create-domain mutation is in flight. */
    isConnecting: boolean;
}

/**
 * Drives the Gmail OAuth connection flow shared by every "Connect Gmail"
 * entry point in the app (Email Sender Settings, and the invoice From
 * sender dropdown): opens the OAuth popup, exchanges the returned code for
 * a Gmail sending domain, and surfaces success/failure toasts. Extracted so
 * every caller reuses one implementation instead of re-triggering OAuth.
 */
export function useConnectGmail({
    onConnected,
}: UseConnectGmailOptions = {}): UseConnectGmailResult {
    const createDomain = useCreateSendingDomain();
    const { toast } = useToaster();
    const [isConnecting, setIsConnecting] = useState(false);
    const isConnectingRef = useRef(false);
    const popupPollRef = useRef<number | null>(null);
    const closeGraceRef = useRef<number | null>(null);

    const clearPopupTimers = useCallback(() => {
        if (popupPollRef.current !== null) {
            window.clearInterval(popupPollRef.current);
            popupPollRef.current = null;
        }
        if (closeGraceRef.current !== null) {
            window.clearTimeout(closeGraceRef.current);
            closeGraceRef.current = null;
        }
    }, []);

    const stopConnecting = useCallback(() => {
        clearPopupTimers();
        isConnectingRef.current = false;
        setIsConnecting(false);
    }, [clearPopupTimers]);

    useEffect(
        function clearPopupTimersOnUnmount() {
            return () => {
                clearPopupTimers();
            };
        },
        [clearPopupTimers],
    );

    const handleAuthorized = useCallback(
        ({ code }: OAuthAuthorizationResponse) => {
            if (!isConnectingRef.current) {
                return;
            }
            stopConnecting();
            void createDomain
                .mutateAsync({
                    method: SendingDomainMethod.gmail,
                    code,
                })
                .then((domain) => {
                    toast("Gmail connected");
                    onConnected?.(domain);
                })
                .catch(() => {
                    toast("Couldn't connect Gmail. Try again.");
                });
        },
        [createDomain, onConnected, toast, stopConnecting],
    );

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: OAuthServiceType.GMAIL,
        handler: handleAuthorized,
    });

    const connect = useCallback(() => {
        if (isConnectingRef.current) {
            return;
        }
        isConnectingRef.current = true;
        setIsConnecting(true);
        const popup = triggerAuthorization(OAuthServiceType.GMAIL);
        if (!popup) {
            stopConnecting();
            return;
        }
        popupPollRef.current = window.setInterval(() => {
            if (!popup.closed || !isConnectingRef.current) {
                return;
            }
            if (popupPollRef.current !== null) {
                window.clearInterval(popupPollRef.current);
                popupPollRef.current = null;
            }
            closeGraceRef.current = window.setTimeout(() => {
                closeGraceRef.current = null;
                if (isConnectingRef.current) {
                    stopConnecting();
                }
            }, POPUP_CLOSE_GRACE_MS);
        }, 500);
    }, [stopConnecting]);

    return {
        connect,
        isConnecting: isConnecting || createDomain.isPending,
    };
}
